// frontend/src/theme/tokens.ts
// ─────────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for every color, spacing token, and font
// definition used in Shield.
//
// To add a new theme in the future:
//   1. Add its id to ThemeId
//   2. Create a DesignTokens object
//   3. Add it to ALL_THEMES
//   That is literally all you need to do.
// ─────────────────────────────────────────────────────────────────────────────

export type ThemeId  = 'midnight' | 'arctic' | 'ember';

export interface DesignTokens {
  id: ThemeId;
  name: string;
  description: string;
  swatchPrimary: string;
  swatchSecondary: string;
  mode: 'dark' | 'light';

  // ── Backgrounds ──────────────────────────────────────────────────────────
  bgCanvas: string;     // outermost page background
  bgSurface: string;    // cards, panels
  bgElevated: string;   // modals, dropdowns
  bgSubtle: string;     // table rows, hover areas

  // ── Brand Colors ─────────────────────────────────────────────────────────
  primary: string;
  primaryHover: string;
  primaryMuted: string; // tinted background for primary elements
  accent: string;       // secondary brand color for highlights

  // ── Semantic Colors ───────────────────────────────────────────────────────
  success: string;
  successMuted: string;
  warning: string;
  warningMuted: string;
  danger: string;
  dangerMuted: string;
  info: string;
  infoMuted: string;

  // ── Text ─────────────────────────────────────────────────────────────────
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textDisabled: string;
  textOnPrimary: string; // text sitting on primary-colored bg

  // ── Borders ──────────────────────────────────────────────────────────────
  borderSubtle: string;
  borderDefault: string;
  borderFocus: string;   // input focus ring

  // ── Sidebar ──────────────────────────────────────────────────────────────
  sidebarBg: string;
  sidebarBorder: string;
  sidebarText: string;
  sidebarTextActive: string;
  sidebarActiveBg: string;
  sidebarHoverBg: string;
  sidebarLogo: string;

  // ── Top Bar ───────────────────────────────────────────────────────────────
  topbarBg: string;
  topbarBorder: string;
  topbarText: string;

  // ── Data Visualization (charts) ───────────────────────────────────────────
  chart1: string;
  chart2: string;
  chart3: string;
  chart4: string;
  chart5: string;

  // ── Gradients ─────────────────────────────────────────────────────────────
  gradientHero: string;
  gradientCard: string;
  gradientSidebar: string;
  gradientButton: string;

  // ── Elevation / Shadows ───────────────────────────────────────────────────
  shadowCard: string;
  shadowElevated: string;
  shadowGlow: string;    // glowing halo around key elements

  // ── Shape ─────────────────────────────────────────────────────────────────
  radiusSm: string;
  radiusMd: string;
  radiusLg: string;
  radiusXl: string;
  radiusFull: string;

  // ── Typography ────────────────────────────────────────────────────────────
  fontDisplay: string;   // headings, logo
  fontBody: string;      // body text, labels
  fontMono: string;      // code, IPs, CVEs
}


// ─────────────────────────────────────────────────────────────────────────────
// THEME 1 — MIDNIGHT
// Aesthetic : Cybersecurity Command Center
// Inspiration: Military SCIF + Bloomberg Terminal dark
// Mood      : Authoritative, focused, zero noise
// Palette   : Deep navy canvas, electric blue primary, cyan accent
// Best for  : Engineers, SOC operators, power users
// ─────────────────────────────────────────────────────────────────────────────
export const midnight: DesignTokens = {
  id:              'midnight',
  name:            'Midnight',
  description:     'Command center dark — built for security engineers',
  swatchPrimary:   '#2563EB',
  swatchSecondary: '#06B6D4',
  mode:            'dark',

  bgCanvas:   '#07101F',
  bgSurface:  '#0D1A2E',
  bgElevated: '#132238',
  bgSubtle:   '#192C47',

  primary:      '#2563EB',
  primaryHover: '#3B82F6',
  primaryMuted: '#1E3A68',
  accent:       '#06B6D4',

  success:      '#10B981',
  successMuted: '#0A2E23',
  warning:      '#F59E0B',
  warningMuted: '#2D2006',
  danger:       '#EF4444',
  dangerMuted:  '#2E1010',
  info:         '#0EA5E9',
  infoMuted:    '#0B2035',

  textPrimary:   '#E8F0FE',
  textSecondary: '#94A3B8',
  textMuted:     '#4B6280',
  textDisabled:  '#2E4060',
  textOnPrimary: '#FFFFFF',

  borderSubtle:  '#162238',
  borderDefault: '#1E3050',
  borderFocus:   '#2563EB',

  sidebarBg:         '#050D1A',
  sidebarBorder:     '#10233A',
  sidebarText:       '#8BAFD4',
  sidebarTextActive: '#FFFFFF',
  sidebarActiveBg:   '#1A3460',
  sidebarHoverBg:    '#0D1A2E',
  sidebarLogo:       '#2563EB',

  topbarBg:     '#0D1A2E',
  topbarBorder: '#162238',
  topbarText:   '#94A3B8',

  chart1: '#2563EB',
  chart2: '#06B6D4',
  chart3: '#10B981',
  chart4: '#F59E0B',
  chart5: '#8B5CF6',

  gradientHero:    'linear-gradient(160deg, #07101F 0%, #0D1A2E 60%, #071828 100%)',
  gradientCard:    'linear-gradient(145deg, #132238 0%, #0D1A2E 100%)',
  gradientSidebar: 'linear-gradient(180deg, #050D1A 0%, #0A1628 100%)',
  gradientButton:  'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',

  shadowCard:     '0 4px 20px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.4)',
  shadowElevated: '0 12px 48px rgba(0,0,0,0.65), 0 4px 12px rgba(0,0,0,0.4)',
  shadowGlow:     '0 0 32px rgba(37,99,235,0.3), 0 0 8px rgba(37,99,235,0.15)',

  radiusSm:   '4px',
  radiusMd:   '8px',
  radiusLg:   '14px',
  radiusXl:   '22px',
  radiusFull: '9999px',

  fontDisplay: "'Syne', 'DM Sans', sans-serif",
  fontBody:    "'DM Sans', 'Manrope', sans-serif",
  fontMono:    "'JetBrains Mono', 'Fira Code', monospace",
};


// ─────────────────────────────────────────────────────────────────────────────
// THEME 2 — ARCTIC
// Aesthetic : Clean Enterprise Boardroom
// Inspiration: Apple + Stripe + Linear light mode
// Mood      : Crisp, trustworthy, executive clarity
// Palette   : Ice white canvas, ink navy primary, emerald accent
// Best for  : Customers, demos, investor presentations
// ─────────────────────────────────────────────────────────────────────────────
export const arctic: DesignTokens = {
  id:              'arctic',
  name:            'Arctic',
  description:     'Clean enterprise light — built for customers & demos',
  swatchPrimary:   '#1E3A8A',
  swatchSecondary: '#059669',
  mode:            'light',

  bgCanvas:   '#F4F7FB',
  bgSurface:  '#FFFFFF',
  bgElevated: '#F0F4F9',
  bgSubtle:   '#E8EDF5',

  primary:      '#1E3A8A',
  primaryHover: '#1D4ED8',
  primaryMuted: '#DBEAFE',
  accent:       '#059669',

  success:      '#059669',
  successMuted: '#D1FAE5',
  warning:      '#D97706',
  warningMuted: '#FEF3C7',
  danger:       '#DC2626',
  dangerMuted:  '#FEE2E2',
  info:         '#0284C7',
  infoMuted:    '#E0F2FE',

  textPrimary:   '#0D1829',
  textSecondary: '#1E293B',
  textMuted:     '#475569',
  textDisabled:  '#CBD5E1',
  textOnPrimary: '#FFFFFF',

  borderSubtle:  '#E8EDF5',
  borderDefault: '#D1D9E5',
  borderFocus:   '#1E3A8A',

  sidebarBg:         '#0F1E38',
  sidebarBorder:     '#1A2E50',
  sidebarText:       '#94B4CC',
  sidebarTextActive: '#FFFFFF',
  sidebarActiveBg:   '#1E3A6E',
  sidebarHoverBg:    '#162B4A',
  sidebarLogo:       '#60A5FA',

  topbarBg:     '#FFFFFF',
  topbarBorder: '#E8EDF5',
  topbarText:   '#0F172A',

  chart1: '#1E3A8A',
  chart2: '#059669',
  chart3: '#0284C7',
  chart4: '#D97706',
  chart5: '#7C3AED',

  gradientHero:    'linear-gradient(160deg, #F4F7FB 0%, #EFF6FF 60%, #F4F7FB 100%)',
  gradientCard:    'linear-gradient(145deg, #FFFFFF 0%, #F4F7FB 100%)',
  gradientSidebar: 'linear-gradient(180deg, #0F1E38 0%, #162B4A 100%)',
  gradientButton:  'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 100%)',

  shadowCard:     '0 1px 6px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.05)',
  shadowElevated: '0 4px 24px rgba(0,0,0,0.10), 0 1px 6px rgba(0,0,0,0.06)',
  shadowGlow:     '0 0 24px rgba(30,58,138,0.15), 0 0 6px rgba(30,58,138,0.08)',

  radiusSm:   '6px',
  radiusMd:   '10px',
  radiusLg:   '16px',
  radiusXl:   '24px',
  radiusFull: '9999px',

  fontDisplay: "'Outfit', 'DM Serif Display', sans-serif",
  fontBody:    "'Nunito Sans', 'Lato', sans-serif",
  fontMono:    "'JetBrains Mono', 'Roboto Mono', monospace",
};


// ─────────────────────────────────────────────────────────────────────────────
// THEME 3 — EMBER
// Aesthetic : Executive Obsidian + Gold
// Inspiration: Bloomberg Terminal amber + Private banking
// Mood      : Premium, serious money, high-stakes ops
// Palette   : Charcoal canvas, molten gold primary, crimson accent
// Best for  : CISOs, financial sector, premium clients
// ─────────────────────────────────────────────────────────────────────────────
export const ember: DesignTokens = {
  id:              'ember',
  name:            'Ember',
  description:     'Premium dark gold — built for executives & enterprise',
  swatchPrimary:   '#D97706',
  swatchSecondary: '#DC2626',
  mode:            'dark',

  bgCanvas:   '#111318',
  bgSurface:  '#181C24',
  bgElevated: '#1F2430',
  bgSubtle:   '#262D3A',

  primary:      '#D97706',
  primaryHover: '#F59E0B',
  primaryMuted: '#3A2506',
  accent:       '#B45309',

  success:      '#16A34A',
  successMuted: '#0D2B18',
  warning:      '#F59E0B',
  warningMuted: '#3A2506',
  danger:       '#DC2626',
  dangerMuted:  '#2E1010',
  info:         '#0EA5E9',
  infoMuted:    '#0A1F32',

  textPrimary:   '#F0EAD6',
  textSecondary: '#9CA3AF',
  textMuted:     '#4B5563',
  textDisabled:  '#2D3340',
  textOnPrimary: '#111318',

  borderSubtle:  '#1E242F',
  borderDefault: '#282F3E',
  borderFocus:   '#D97706',

  sidebarBg:         '#0C0F14',
  sidebarBorder:     '#181C24',
  sidebarText:       '#9CA3AF',
  sidebarTextActive: '#F0EAD6',
  sidebarActiveBg:   '#3A2506',
  sidebarHoverBg:    '#181C24',
  sidebarLogo:       '#D97706',

  topbarBg:     '#181C24',
  topbarBorder: '#1E242F',
  topbarText:   '#9CA3AF',

  chart1: '#D97706',
  chart2: '#F59E0B',
  chart3: '#16A34A',
  chart4: '#DC2626',
  chart5: '#8B5CF6',

  gradientHero:    'linear-gradient(160deg, #111318 0%, #181C24 60%, #14181F 100%)',
  gradientCard:    'linear-gradient(145deg, #1F2430 0%, #181C24 100%)',
  gradientSidebar: 'linear-gradient(180deg, #0C0F14 0%, #111318 100%)',
  gradientButton:  'linear-gradient(135deg, #D97706 0%, #B45309 100%)',

  shadowCard:     '0 4px 20px rgba(0,0,0,0.6), 0 1px 4px rgba(0,0,0,0.5)',
  shadowElevated: '0 12px 48px rgba(0,0,0,0.7), 0 4px 16px rgba(0,0,0,0.5)',
  shadowGlow:     '0 0 32px rgba(217,119,6,0.25), 0 0 8px rgba(217,119,6,0.15)',

  radiusSm:   '3px',
  radiusMd:   '6px',
  radiusLg:   '12px',
  radiusXl:   '18px',
  radiusFull: '9999px',

  fontDisplay: "'Cormorant Garamond', 'Playfair Display', serif",
  fontBody:    "'IBM Plex Sans', 'Karla', sans-serif",
  fontMono:    "'JetBrains Mono', 'Courier New', monospace",
};


// ─────────────────────────────────────────────────────────────────────────────
// REGISTRY — add new themes here in the future
// ─────────────────────────────────────────────────────────────────────────────
export const ALL_THEMES: Record<ThemeId, DesignTokens> = {
  midnight,
  arctic,
  ember,
};

export const DEFAULT_THEME: ThemeId = 'arctic';
